// The daily seed.
//
// One run, derived from the date, identical for everybody. That is the
// whole feature: it turns "I got 2:41" from a private number into a
// comparable one, because we both fought the same fight.
//
// ## UTC, not local time
//
// The rollover has to happen at the same *instant* everywhere or the
// shared-context promise quietly breaks: a player in Auckland would be
// on tomorrow's seed while a player in Los Angeles is still on today's,
// and the two of them would compare times for runs that were never the
// same run. So the day key is UTC, always. This mirrors `utcDayKey` in
// `mascot-engine.ts`, which got this right for the mascot's daily
// challenge; the logic is reimplemented rather than imported because
// `src/lib/daemon/` is meant to stand alone (it also ships standalone
// at daemon.skenmy.com, without the rest of skenOS behind it).
//
// ## Recorded vs practice
//
// One recorded attempt per day, and as many practice attempts as you
// like. Locking practice out would be hostile — the point of a fixed
// seed is that it is learnable — but a leaderboard where everyone gets
// unlimited retries measures patience, not skill. So both exist, and
// they are flagged separately: practice runs are stored and ghosted,
// they simply never become the day's record.

import { NULL_HOST, type DaemonHost } from "./host";
import { TICK_HZ, type NiceLevel, type RunConfig, type RunStatus } from "./types";

// ── The clock ─────────────────────────────────────────────────────────

export const DAY_MS = 86_400_000;

/**
 * `YYYY-MM-DD` in UTC. Every function in this file takes the day as a
 * string so tests can pin it; nothing here reads a clock it wasn't
 * handed except through this default.
 */
export function utcDayKey(now: Date = new Date()): string {
  const t = now.getTime();
  const safe = Number.isFinite(t) ? now : new Date(0);
  return safe.toISOString().slice(0, 10);
}

/** Milliseconds until the seed rolls over. For the countdown in the
 *  HUD, which is the only honest way to say "you have until". */
export function msUntilRollover(now: Date = new Date()): number {
  const t = now.getTime();
  if (!Number.isFinite(t)) return DAY_MS;
  return DAY_MS - (((t % DAY_MS) + DAY_MS) % DAY_MS);
}

// ── Hashing ───────────────────────────────────────────────────────────

/** FNV-1a, 32-bit. Stable across engines, which is the entire
 *  requirement: every visitor's browser must derive the same seed from
 *  the same date. */
export function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** mulberry32. Same choice `mascot-engine.ts` makes for the daily
 *  challenge: six lines, decent distribution, and reproducible from a
 *  date alone — which `Math.random` is definitionally not. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ── The seed itself ───────────────────────────────────────────────────

/**
 * Seed words. All-caps, all UNIX, all pronounceable — the seed is meant
 * to be said out loud ("today's is SIGPIPE-42") and typed back in from
 * memory, which rules out both hex digests and cute nonsense.
 */
export const SEED_WORDS: string[] = [
  "SIGPIPE",
  "SIGHUP",
  "SIGBUS",
  "SEGFAULT",
  "ORPHAN",
  "ZOMBIE",
  "INITRD",
  "RUNLEVEL",
  "CGROUP",
  "SYSCALL",
  "KERNEL",
  "PANIC",
  "FSCK",
  "INODE",
  "DAEMON",
  "PTRACE",
  "EPOLL",
  "MMAP",
  "REAPER",
  "SWAPPER",
  "TTY",
  "CORE",
  "NICE",
  "FORKBOMB",
];

/**
 * Difficulties the daily draws from. Not the full `NiceLevel` range on
 * purpose — a daily at nice -20 would be a coin flip nobody clears, and
 * a daily at 19 wouldn't separate anyone.
 */
export const DAILY_NICE_POOL: NiceLevel[] = [10, 5, 0, -5];

export type DailyRun = {
  /** The UTC day this belongs to. */
  day: string;
  /** Human-readable, shareable, typeable. e.g. `SIGPIPE-42`. */
  seed: string;
  nice: NiceLevel;
};

/** Deterministic: same UTC day in, same run out, in every browser on
 *  the planet. */
export function dailyRun(day: string = utcDayKey()): DailyRun {
  const rng = mulberry32(hashString("skenos.daemon.daily:" + day));
  const word = SEED_WORDS[Math.floor(rng() * SEED_WORDS.length)] ?? SEED_WORDS[0]!;
  // Two digits, so the seed is short enough to read aloud but wide
  // enough that the same word on two days is obviously a different run.
  const n = 10 + Math.floor(rng() * 90);
  const nice = DAILY_NICE_POOL[Math.floor(rng() * DAILY_NICE_POOL.length)] ?? 0;
  return { day, seed: `${word}-${n}`, nice };
}

/** The `RunConfig` for a daily. `daily: true` is what routes the result
 *  into the recorded-attempt slot rather than the free-play list. */
export function dailyConfig(run: DailyRun = dailyRun()): RunConfig {
  return { seed: run.seed, nice: run.nice, modifier: null, daily: true };
}

// ── Typed-in and linked seeds ─────────────────────────────────────────

/**
 * Canonicalise a seed a human typed or pasted.
 *
 * Uppercased, non-seed characters dropped, capped in length. This
 * matters more than it looks: the seed string is hashed directly, so
 * `sigpipe-42` and `SIGPIPE-42` would otherwise be two different fights
 * that look like the same one — which is exactly the confusion the
 * daily exists to prevent.
 */
export function normalizeSeed(raw: string): string {
  return (raw ?? "")
    .toUpperCase()
    .replace(/[^A-Z0-9-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 24);
}

/** Read one parameter out of a query string. Accepts a full URL, a
 *  `location.search`, or a bare `k=v`, because all three turn up in
 *  practice (shared link, in-app router, someone's terminal). */
function param(search: string | null | undefined, name: string): string | null {
  if (!search) return null;
  const q = search.includes("?") ? search.slice(search.indexOf("?") + 1) : search;
  for (const part of q.split("&")) {
    const eq = part.indexOf("=");
    if (eq < 0) continue;
    if (part.slice(0, eq) !== name) continue;
    const value = part.slice(eq + 1).replace(/\+/g, " ");
    try {
      return decodeURIComponent(value);
    } catch {
      return value; // a stray % — take it literally
    }
  }
  return null;
}

/** Pull `?seed=` out of a query string, normalised. */
export function seedFromSearch(search: string | null | undefined): string | null {
  const raw = param(search, "seed");
  return raw ? normalizeSeed(raw) || null : null;
}

/** All the difficulties a run may be started at. */
export const NICE_LEVELS: NiceLevel[] = [19, 10, 5, 0, -5, -10, -15, -20];

/** The difficulty a link without one gets. Mid-table: hard enough to be
 *  a fight, kind enough that a stranger who clicked a link sees more
 *  than the first ten seconds. */
export const DEFAULT_NICE: NiceLevel = 5;

/**
 * Pull `?nice=` out of a query string.
 *
 * A seed on its own does not describe a fight — `niceScale` moves
 * bullet speed, telegraph length and DAEMON's HP — so a shared link
 * that omits the difficulty is a link to a *different* run than the one
 * the sender played. Anything unrecognised is treated as absent rather
 * than clamped, because guessing at what someone meant by `?nice=7`
 * silently produces a third, wronger run.
 */
export function niceFromSearch(search: string | null | undefined): NiceLevel | null {
  const raw = param(search, "nice");
  if (raw === null || raw.trim() === "") return null;
  const n = Number(raw);
  return NICE_LEVELS.find((v) => v === n) ?? null;
}

/** A shareable link, relative so it works in both hosts. Carries the
 *  difficulty, because the seed alone is not the fight. */
export function seedLink(
  seed: string,
  opts: { nice?: NiceLevel; base?: string } = {},
): string {
  const q = `?seed=${encodeURIComponent(normalizeSeed(seed))}`;
  return `${opts.base ?? ""}${q}${opts.nice === undefined ? "" : `&nice=${opts.nice}`}`;
}

export type ResolvedRun = {
  config: RunConfig;
  /** The day whose daily this is, or null for a typed-in seed. */
  day: string | null;
  /** True when this is the day's official run rather than free play. */
  isDaily: boolean;
};

/**
 * What run should we start? A `?seed=` in the URL wins — that is
 * someone following a link to a specific fight, and silently handing
 * them the daily instead would be a small betrayal. Otherwise, today's.
 *
 * A link that names today's seed *at today's difficulty* is the daily,
 * however it was arrived at; the same seed at another difficulty is not,
 * and must not consume the day's one recorded attempt.
 */
export function resolveRun(
  search?: string | null,
  now: Date = new Date(),
  fallbackNice: NiceLevel = DEFAULT_NICE,
): ResolvedRun {
  const day = utcDayKey(now);
  const linked = seedFromSearch(search);
  const linkedNice = niceFromSearch(search);
  const today = dailyRun(day);

  const isToday =
    !linked || (linked === today.seed && (linkedNice === null || linkedNice === today.nice));
  if (isToday) return { config: dailyConfig(today), day, isDaily: true };

  return {
    config: { seed: linked, nice: linkedNice ?? fallbackNice, modifier: null, daily: false },
    day: null,
    isDaily: false,
  };
}

// ── Attempts ──────────────────────────────────────────────────────────

export type DailyMode = "recorded" | "practice";

export type DailyAttempt = {
  /** In-game ticks. The only clock that counts. */
  ticks: number;
  status: RunStatus;
  /** The run code, so the day's result is shareable straight from the
   *  results screen without re-deriving anything. */
  code: string;
  /** Epoch seconds, for display only. */
  at: number;
};

export type DailyState = {
  day: string;
  seed: string;
  /** The one attempt that counts. Null until it's been used. */
  recorded: DailyAttempt | null;
  /** How many practice runs today. Unlimited, but worth showing. */
  practice: number;
  /** Best practice attempt, so practising still feels like progress. */
  bestPractice: DailyAttempt | null;
};

export const DAILY_STORE_SUFFIX = "daily.v1";

export function emptyDailyState(run: DailyRun): DailyState {
  return { day: run.day, seed: run.seed, recorded: null, practice: 0, bestPractice: null };
}

function safeStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    // Sandboxed iframes throw on access, not just on use.
    return null;
  }
}

const key = (host: DaemonHost): string => host.storageKey(DAILY_STORE_SUFFIX);

const isAttempt = (a: unknown): a is DailyAttempt =>
  !!a &&
  typeof (a as DailyAttempt).ticks === "number" &&
  Number.isFinite((a as DailyAttempt).ticks) &&
  typeof (a as DailyAttempt).status === "string";

/**
 * Read today's state, or a fresh one if what's stored is for another
 * day. A hard reset on rollover — carrying yesterday's attempt forward
 * would silently deny someone their run.
 */
export function loadDailyState(
  run: DailyRun,
  host: DaemonHost = NULL_HOST,
  storage?: Storage | null,
): DailyState {
  const s = storage === undefined ? safeStorage() : storage;
  if (!s) return emptyDailyState(run);
  try {
    const raw = s.getItem(key(host));
    if (raw) {
      const p = JSON.parse(raw) as Partial<DailyState>;
      if (p && p.day === run.day && p.seed === run.seed) {
        return {
          day: run.day,
          seed: run.seed,
          recorded: isAttempt(p.recorded) ? p.recorded : null,
          practice: Math.max(0, Math.floor(Number(p.practice) || 0)),
          bestPractice: isAttempt(p.bestPractice) ? p.bestPractice : null,
        };
      }
    }
  } catch {
    /* corrupt payload — start the day clean */
  }
  return emptyDailyState(run);
}

export function saveDailyState(
  state: DailyState,
  host: DaemonHost = NULL_HOST,
  storage?: Storage | null,
): void {
  const s = storage === undefined ? safeStorage() : storage;
  if (!s) return;
  try {
    s.setItem(key(host), JSON.stringify(state));
  } catch {
    /* quota, private mode — the run still happened */
  }
}

/** Has the day's one recorded attempt been used? */
export const canRecord = (state: DailyState): boolean => state.recorded === null;

/** Better = cleared it, or survived longer if nobody cleared it. */
function better(a: DailyAttempt, b: DailyAttempt): boolean {
  const aWon = a.status === "won";
  const bWon = b.status === "won";
  if (aWon !== bWon) return aWon;
  return aWon ? a.ticks < b.ticks : a.ticks > b.ticks;
}

/**
 * Fold a finished run into today's state. Returns a new state; the
 * caller persists it. A recorded attempt when one already exists is
 * quietly demoted to practice rather than rejected — the run happened,
 * and throwing it away would be worse than not counting it.
 */
export function applyAttempt(
  state: DailyState,
  attempt: DailyAttempt,
  mode: DailyMode,
): DailyState {
  if (mode === "recorded" && canRecord(state)) {
    return { ...state, recorded: attempt };
  }
  const best = state.bestPractice && !better(attempt, state.bestPractice)
    ? state.bestPractice
    : attempt;
  return { ...state, practice: state.practice + 1, bestPractice: best };
}

/** Seconds of in-game time, from ticks. For results-screen copy that
 *  wants a number rather than a formatted string. */
export const ticksToSeconds = (ticks: number): number => ticks / TICK_HZ;
